import { memo, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, Input } from 'antd'
import { clsx } from 'clsx'

import CustomButton from '@/components/CustomButton'
import ImageWithFallBack from '@/components/ImageWithFallback'
import { ProductService } from '@/services'
import { useAsync } from '@/hooks'
import useSessionBased from '@/hooks/useSessionBased'
import { ASYNC_STATUS } from '@/libs/constants'
import { isMaterialNumberValid } from '@/libs/utils/material-number'

import styles from './SparePartsProductInfo.module.scss'

interface SparePartsProductInfoProps {
  materialNumber: string
  handleSetIconState?: (...args: unknown[]) => unknown
}

const SparePartsProductInfo = (props: SparePartsProductInfoProps) => {
  const { materialNumber, handleSetIconState } = props

  const { sessionId, hasSessionBased } = useSessionBased()
  const { t } = useTranslation()

  const [getProductInfoStatus, setGetProductInfoStatus] = useState(
    ASYNC_STATUS.IDLE,
  )

  const getDisisLink = useCallback(async () => {
    return ProductService.getProductSpareParts(materialNumber, sessionId)
  }, [sessionId, materialNumber])

  const {
    execute: executeGetDisisLink,
    value: disisResponse,
    status: getDisisLinkStatus,
  } = useAsync(getDisisLink, false)

  const handleClick = (event) => {
    event.preventDefault()
    executeGetDisisLink()
  }

  const getBasicProductInfo = useCallback(async () => {
    try {
      if (!!hasSessionBased && !sessionId) {
        return Promise.reject(new Error('Missing Session ID'))
      }
      if (!isMaterialNumberValid(materialNumber)) {
        return Promise.reject(new Error('Invalid Material Number'))
      }

      const response = await ProductService.getBasicProductInfo(materialNumber)
      setGetProductInfoStatus(ASYNC_STATUS.SUCCESS)
      return {
        ...response.data,
      }
    } catch (error) {
      setGetProductInfoStatus(ASYNC_STATUS.ERROR)
      throw error
    }
  }, [hasSessionBased, sessionId, materialNumber])

  const { value, status, error } = useAsync(getBasicProductInfo, true)

  useEffect(() => {
    handleSetIconState?.(getProductInfoStatus)
  }, [getProductInfoStatus, handleSetIconState])

  useEffect(() => {
    if (!isMaterialNumberValid(materialNumber) && materialNumber !== '') {
      handleSetIconState?.(ASYNC_STATUS.IDLE)
    }
  }, [materialNumber, handleSetIconState])

  useEffect(() => {
    if (disisResponse) {
      const disisLink = disisResponse.data
      window.open(disisLink, '_blank', 'noopener noreferrer')
    }
  }, [disisResponse])

  if (status === 'pending') {
    return (
      <div className={styles.sparePartsProductInfo}>
        {t('form.placeHolder.loading')}
      </div>
    )
  }

  if (!isMaterialNumberValid(materialNumber) && value !== '') {
    return (
      <div className={clsx([styles.sparePartsProductInfo, styles.error])}>
        {t('error.invalidMaterialNumber')}
      </div>
    )
  }

  if (error) {
    return (
      <div className={clsx([styles.sparePartsProductInfo, styles.error])}>
        {t('cart.productNotAvailable')}
      </div>
    )
  }

  if (!value) {
    return <div />
  }

  return (
    <div className={styles.sparePartsProductInfo}>
      <div className={styles.productImage}>
        <ImageWithFallBack src={value?.pictureUrl} />
      </div>
      <Form.Item
        className="custom-form-item"
        label="Product Name"
        labelCol={{ span: 24 }}
      >
        <Input disabled value={value?.materialName} />
      </Form.Item>
      <a href="/#">
        <CustomButton
          loading={getDisisLinkStatus === ASYNC_STATUS.PENDING}
          onClick={handleClick}
        >
          {t('sparePartsFinder.button.toSpareParts')}
        </CustomButton>
      </a>
    </div>
  )
}

export default memo(SparePartsProductInfo)
